import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, type NavItem, type RadarRow } from "../api";
import { hasCommandModifier, modifierLabel } from "../platform";

/**
 * Go anywhere by typing. ⌘K / Ctrl-K.
 *
 * The radar answers "what needs me"; this answers "take me to CE-018",
 * which is the other half of how somebody uses a contract tool and until
 * now meant loading a list and hunting down it.
 *
 * It searches what a person actually names: an event reference, the words
 * in its title, a clause number, or a screen. Nothing is fetched until it
 * is opened, so the cost is paid by people who use it.
 */
interface Entry {
  label: string;
  hint: string;
  to: string;
  kind: "event" | "screen";
}

export function Palette({ nav }: { nav: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [events, setEvents] = useState<RadarRow[] | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (hasCommandModifier(e) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((was) => !was);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Loaded on first open, not on every page load.
  useEffect(() => {
    if (!open || events !== null) return;
    get<{ rows: RadarRow[] }>("/radar")
      .then((d) => setEvents(d.rows))
      .catch(() => setEvents([]));
  }, [open, events]);

  useEffect(() => {
    if (open) { setQuery(""); setCursor(0); input.current?.focus(); }
  }, [open]);

  const entries = useMemo<Entry[]>(() => {
    const seen = new Set<string>();
    const out: Entry[] = [];
    for (const row of events ?? []) {
      if (seen.has(row.reference)) continue;
      seen.add(row.reference);
      out.push({
        label: row.reference,
        hint: row.title || `${row.clock} · clause ${row.clause}`,
        to: `/events/${encodeURIComponent(row.reference)}`,
        kind: "event",
      });
    }
    for (const item of nav) {
      if (item.href.startsWith("/docs")) continue;
      out.push({ label: item.label, hint: "screen", to: item.href, kind: "screen" });
    }
    return out;
  }, [events, nav]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries.slice(0, 12);
    return entries
      .filter((e) => `${e.label} ${e.hint}`.toLowerCase().includes(q))
      .slice(0, 12);
  }, [entries, query]);

  useEffect(() => setCursor(0), [query]);

  if (!open) return null;

  function go(entry: Entry | undefined) {
    if (!entry) return;
    setOpen(false);
    navigate(entry.to);
  }

  return (
    <div className="palette__scrim" role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Go to">
        <input ref={input} className="palette__input" value={query}
          placeholder="Go to an event, a clause, or a screen…"
          aria-label="Go to an event, a clause, or a screen"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
            if (e.key === "ArrowDown" || (e.key === "n" && e.ctrlKey)) {
              e.preventDefault(); setCursor((c) => Math.min(matches.length - 1, c + 1));
            }
            if (e.key === "ArrowUp" || (e.key === "p" && e.ctrlKey)) {
              e.preventDefault(); setCursor((c) => Math.max(0, c - 1));
            }
            if (e.key === "Enter") { e.preventDefault(); go(matches[cursor]); }
          }} />

        {events === null && <div className="palette__empty">Loading…</div>}
        {events !== null && matches.length === 0 && (
          <div className="palette__empty">Nothing matches “{query}”.</div>
        )}

        <ul className="palette__list" role="listbox">
          {matches.map((entry, i) => (
            <li key={entry.to + entry.label} role="option" aria-selected={i === cursor}
              className={`palette__row${i === cursor ? " on" : ""}`}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => { e.preventDefault(); go(entry); }}>
              <span className={entry.kind === "event" ? "mn" : ""}>{entry.label}</span>
              <span className="palette__hint">{entry.hint}</span>
            </li>
          ))}
        </ul>
        <div className="palette__foot">
          <kbd>↑</kbd><kbd>↓</kbd> move · <kbd>↵</kbd> open · <kbd>esc</kbd> close
          <span className="palette__foot-right">
            <kbd>{modifierLabel()}</kbd><kbd>K</kbd> toggles this
          </span>
        </div>
      </div>
    </div>
  );
}
